import CategoryBanner, { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import PostitWidget, { PostitWidgetProps } from 'components/Others/PostitWidget';
import { getSparePartSlug } from 'functions/handleSparePart';
import { wcApiDataService } from 'functions/wcApi';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState, useMemo } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import { getPageHeaderData, getSeoBlockContent, getSeoMetaTags, getCategoryBannerImages } from 'utils/common.utils';
import { generateImage } from 'utils/image.utils';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import ProductCard from 'components/ProductCard/ProductCard';
import { getLocalCurrency } from 'functions/currency';
import useWindowSize from 'hooks/useWindowSize';
import { addDataLayer } from 'lib/ga';
import { SeoBlock, SeoMetaTags } from 'constants/dataTypes';
import CategorySeoText from 'components/Others/CategorySeoText';
import FAQJson from 'components/Others/FAQJson';
import ProductCardwLensColour from 'components/ProductCard/ProductCardwLensColour';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import CategoryBannerImageComponent from 'components/CategoryBannerImage/CategoryBannerImage';
import { CategoryBannerImage } from 'constants/dataTypes';
import React from 'react';
//import ColorFilter from 'components/ColorFilter/ColorFilter';
const ColorFilter = dynamic(
  () => import('components/ColorFilter/ColorFilter')
);
const RecommendedProductsSlider = dynamic(
  () => import('components/Others/RecommendedProductsSlider')
);

export default function ShopCategory({
  categories,
  seoBlock,
  seoMetaTags,
  categoryBannerProps,
  categoryBannerImages
}: {
  categories: any;
  seoBlock: SeoBlock;
  seoMetaTags: SeoMetaTags;
  categoryBannerProps: CategoryBannerProps;
  categoryBannerImages: CategoryBannerImage[];
}) {


  const postitWidgetProps: PostitWidgetProps[] = categories.meta?.postit_notes;
  const { width } = useWindowSize();
  const router = useRouter();

  const data = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'shop',
      text: 'Shop'
    },
    {
      id: 3,
      href: 'shop/spare-parts',
      text: `${categories && categories.name}`,
      forceLink: router.query.colour != null && typeof router.query.colour === 'object' ? true : false
    }
  ];


  const [selectedSlugs, setSelectedSlugs] = useState<Array<string>>([]);
  // const sizes = ['Extra Small', 'Small', 'Medidum', 'Large'];

  useEffect(() => {
    var categoryProducts: any = [];
    var position = 1;

    categories.meta.category_glasses_color &&
      categories.meta.category_glasses_color.map((prod: any) => {
        categoryProducts.push({
          name: prod.name,
          price: categories.meta.category_price_reference.price,
          regularPrice: categories.meta.category_price_reference.price,
          category: categories?.name,
          list: 'Category',
          position: position
        });
        position++;
      });

    addDataLayer({
      event: 'ImpCompleted',
      ecommerce: {
        currencyCode: getLocalCurrency(router.locale),
        impressions: categoryProducts
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const indexedBanners = useMemo(() => {
    const bannerMap = new Map<number, CategoryBannerImage>();

    categoryBannerImages.forEach(banner => {
      if (banner.bannerType === 'small-square') {
        bannerMap.set(banner.orderIndex, banner);
      } else {
        const nextMultipleOfThree = Math.ceil(banner.orderIndex / 3) * 3 + 1;
        bannerMap.set(nextMultipleOfThree, banner);
      }
    });

    return bannerMap;
  }, [categoryBannerImages]);

  let categoryBannerImageCount = 0;

  if (router.isFallback) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div>
      <Head>
        <title>Spare Parts | Dresden Vision</title>
        <meta name="twitter:title" content={`Spare Parts | Dresden Vision`} />
        <meta name="og:title" content={`Spare Parts | Dresden Vision`} />
        <meta
          name="description"
          content={`Spare Part models with affordable prices and its wide selection are on this page. Start reviewing Spare Parts in various color options.`}
        />
        <meta
          property="og:description"
          content={`Spare Part models with affordable prices and its wide selection are on this page. Start reviewing Spare Parts in various color options.`}
        />
        <meta
          name="twitter:description"
          content={`Spare Part models with affordable prices and its wide selection are on this page. Start reviewing Spare Parts in various color options.`}
        />
      </Head>
      <CategoryBanner {...categoryBannerProps} breadcrumbItems={data} />
      <div className="mx-auto max-w-[1224px] bg-white px-4 pt-4">
        {/* Remove the Breadcrumb component from here */}
        <div className="mt-[24px] grid grid-cols-1 gap-y-[20px] md:grid-cols-2 md:gap-x-[15px] md:gap-y-[30px] shop:grid-cols-3">
          {categories.meta.category_glasses_color &&
            categories.meta.category_glasses_color
              .filter((color: { slug: string; }) => {
                if (selectedSlugs.length > 0 && !selectedSlugs.includes(color.slug)) {
                  return false;
                }
                return true;
              })
              .map((prod: any, index: number) => {
                const currentPosition = index + 1 + categoryBannerImageCount;
                const bannerImage = indexedBanners.get(currentPosition);
                if (bannerImage) {
                  categoryBannerImageCount++;
                }
                return (
                  <React.Fragment key={`color-${prod.slug}-${index}`}>
                    {bannerImage && (
                      <div className={bannerImage.bannerType === 'large-rectangle' ? 'col-span-full' : undefined}>
                        <CategoryBannerImageComponent banner={bannerImage} width={width} />
                      </div>
                    )}
                    {/* {postitWidgetProps.map((postitWidget) => (
                      postitWidget.sort_index == (index + 1) ?
                        <LazyHydrate whenVisible key={index + '_widget-lh'}>
                          <PostitWidget key={index + '_widget'} {...postitWidget} />
                        </LazyHydrate> :
                        <></>
                    ))} */}
                    <LazyHydrate whenVisible key={index + '_card-lh'}>
                      <ProductCardwLensColour
                        onClickPoroduct={() => {
                          addDataLayer({
                            event: 'productClick',
                            ecommerce: {
                              click: {
                                actionField: { list: 'category' },
                                products: [
                                  {
                                    name: prod.name,
                                    price: categories.meta.category_price_reference.price,
                                    category: categories?.name,
                                    list: 'Category',
                                    position: index + 1
                                  }
                                ]
                              }
                            }
                          });
                        }}
                        key={index}
                        width={width}
                        data={{
                          title: prod.name,
                          price: categories.meta.category_price_reference.price,
                          regularPrice: categories.meta.category_price_reference.regular_price,
                          images: [`${prod.image?.mediaItemUrl}`],
                          href: `/shop/${categories.slug}/${getSparePartSlug(prod.slug)}`,
                          prio: width > 600 ? index <= 1 : index == 0,
                          discountTag: false
                        }}
                      />
                    </LazyHydrate>
                  </React.Fragment>
                );
              })}
        </div>
        <LazyHydrate whenVisible>
          <RecommendedProductsSlider />
        </LazyHydrate>
        <LazyHydrate whenVisible>
          <CategorySeoText seoBlock={seoBlock} />
        </LazyHydrate>
        <FAQJson faq={seoBlock.faq} />
        <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
      </div>
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  var categories: any = await wcApiDataService('cat/spare-parts', '', locale);
  if (!categories) {
    return {
      notFound: true
    };
  }

  const seoBlock = await getSeoBlockContent('spare-parts', locale);

  const seoMetaTags = await getSeoMetaTags('/shop/spare-parts', locale);
  const categoryBannerImages = await getCategoryBannerImages('spare-parts', locale);


  const categoryBannerProps: CategoryBannerProps = {
    title: categories.name,
    description: categories.description,
    imageSrc: generateImage(categories.slug, 'categories', 'slug', 'banner', 'jpg')
  }

  const notionPageHeaders = await getPageHeaderData('spare-parts', locale, categoryBannerProps);


  return {
    props: {
      categories,
      seoBlock,
      seoMetaTags,
      categoryBannerProps: notionPageHeaders,
      categoryBannerImages
    },
    //revalidate: 600
  };
};
